<template>
  <div class="users-view">
    <div class="users-toolbar">
      <h2>Users</h2>

      <v-spacer></v-spacer>

      <v-select
        v-if="isAdmin"
        v-model="selectedAppId"
        :items="filterAppOptions"
        item-text="name"
        item-value="id"
        label="App"
        dense
        outlined
        hide-details
        class="users-toolbar__app"
        @change="load"
      ></v-select>

      <v-btn color="primary" depressed @click="openCreate">
        <v-icon left>mdi-account-plus</v-icon>
        Add User
      </v-btn>
    </div>

    <SearchBar label="Search for user" @search="search = $event" />

    <v-data-table
      :headers="headers"
      :items="users"
      :search="search"
      :loading="isLoading"
      item-key="id"
      class="elevation-1 mt-5"
    >
      <template v-slot:[`item.displayName`]="{ item }">
        <div class="users-table__name">{{ fullName(item) }}</div>
        <div class="users-table__email">{{ item.email }}</div>
      </template>

      <template v-slot:[`item.role`]="{ item }">
        <v-chip small color="primary" text-color="white">{{ item.role }}</v-chip>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon small v-bind="attrs" v-on="on" @click="openEdit(item)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
          </template>
          <span>Edit</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon small v-bind="attrs" v-on="on" @click="openPasswordReset(item)">
              <v-icon small>mdi-lock-reset</v-icon>
            </v-btn>
          </template>
          <span>Reset password</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon small v-bind="attrs" v-on="on" @click="removeUser(item)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </template>
          <span>Delete</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <v-dialog v-model="userDialog" max-width="640">
      <v-card>
        <v-card-title>{{ editing ? 'Edit User' : 'Add User' }}</v-card-title>
        <v-card-text>
          <v-form ref="userForm" v-model="valid" lazy-validation @submit.prevent="saveUser">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.firstname" label="First name" :rules="nameRules" outlined dense></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.lastname" label="Last name" :rules="nameRules" outlined dense></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field v-model="form.email" label="Email" :rules="emailRules" outlined dense></v-text-field>
              </v-col>
              <v-col v-if="!editing" cols="12">
                <v-text-field
                  v-model="form.password"
                  label="Password"
                  type="password"
                  :rules="passwordRules"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.role"
                  :items="roles"
                  label="Role"
                  :rules="roleRules"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col v-if="isAdmin" cols="12" md="6">
                <v-select
                  v-model="form.appId"
                  :items="appOptions"
                  item-text="name"
                  item-value="id"
                  label="App"
                  :rules="appRules"
                  outlined
                  dense
                ></v-select>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="userDialog = false">Cancel</v-btn>
          <v-btn color="primary" depressed :loading="isSaving" @click="saveUser">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="passwordDialog" max-width="480">
      <v-card>
        <v-card-title>Reset Password</v-card-title>
        <v-card-text>
          <v-form ref="passwordForm" v-model="passwordValid" lazy-validation @submit.prevent="savePassword">
            <v-text-field
              v-model="passwordForm.newPassword"
              label="New password"
              type="password"
              :rules="passwordRules"
              outlined
              dense
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="passwordDialog = false">Cancel</v-btn>
          <v-btn color="primary" depressed :loading="isSaving" @click="savePassword">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import SearchBar from '@/components/SearchBar.vue';
import { accountService, appService, userService } from '@/services';

export default {
  name: 'Users',
  components: {
    SearchBar
  },
  data: () => ({
    isLoading: false,
    isSaving: false,
    isAdmin: false,
    selectedAppId: null,
    users: [],
    apps: [],
    roles: [],
    search: '',
    userDialog: false,
    passwordDialog: false,
    editing: false,
    valid: false,
    passwordValid: false,
    selectedUser: null,
    form: {
      id: null,
      firstname: '',
      lastname: '',
      email: '',
      password: '',
      role: 'Staff',
      appId: null
    },
    passwordForm: {
      newPassword: ''
    },
    nameRules: [v => !!v || 'Required'],
    roleRules: [v => !!v || 'Role is required'],
    appRules: [v => !!v || 'App is required'],
    emailRules: [
      v => !!v || 'Email is required',
      v => /^(([^<>()[\]\\.,;:\s@']+(\.[^<>()\\[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(v) || 'Email must be valid',
    ],
    passwordRules: [
      v => !!v || 'Password is required',
      v => /(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{6,}/.test(v) || 'Password must contain at least one lowercase letter, one number and one uppercase letter',
    ],
  }),
  computed: {
    appOptions() {
      return this.apps.map(app => ({
        id: app.id,
        name: app.name
      }));
    },
    filterAppOptions() {
      return [
        { id: null, name: 'All apps' },
        ...this.appOptions
      ];
    },
    headers() {
      const headers = [
        { text: 'User', align: 'start', sortable: true, filterable: true, value: 'displayName' },
        { text: 'Role', align: 'start', sortable: true, filterable: true, value: 'role' },
      ];

      if (this.isAdmin) {
        headers.push({ text: 'App', align: 'start', sortable: true, filterable: true, value: 'appName' });
      }

      headers.push({ text: 'Actions', align: 'end', sortable: false, value: 'actions' });
      return headers;
    }
  },
  async created() {
    this.isAdmin = userService.isAdmin();
    await this.loadSetup();
    await this.load();
  },
  methods: {
    async loadSetup() {
      this.roles = await accountService.getRoles();

      if (this.isAdmin) {
        this.apps = await appService.getAll();
      } else {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        this.selectedAppId = user.appId;
      }
    },
    async load() {
      this.isLoading = true;
      this.users = await accountService.getUsers(this.isAdmin ? this.selectedAppId : null);
      this.isLoading = false;
    },
    fullName(user) {
      const name = `${user.firstname || ''} ${user.lastname || ''}`.trim();
      return name || user.email;
    },
    resetForm() {
      this.form = {
        id: null,
        firstname: '',
        lastname: '',
        email: '',
        password: '',
        role: this.roles.includes('Staff') ? 'Staff' : this.roles[0],
        appId: this.selectedAppId
      };
    },
    openCreate() {
      this.editing = false;
      this.resetForm();
      this.userDialog = true;
      this.$nextTick(() => {
        if (this.$refs.userForm) {
          this.$refs.userForm.resetValidation();
        }
      });
    },
    openEdit(user) {
      this.editing = true;
      this.form = {
        id: user.id,
        firstname: user.firstname,
        lastname: user.lastname,
        email: user.email,
        password: '',
        role: user.role,
        appId: user.appId
      };
      this.userDialog = true;
      this.$nextTick(() => {
        if (this.$refs.userForm) {
          this.$refs.userForm.resetValidation();
        }
      });
    },
    async saveUser() {
      if (!this.$refs.userForm.validate()) return;

      this.isSaving = true;
      const response = this.editing
        ? await accountService.update(this.form)
        : await accountService.create(this.form);
      this.isSaving = false;

      if (response.status != 200) {
        await this.showError(response);
        return;
      }

      this.userDialog = false;
      await this.load();
    },
    openPasswordReset(user) {
      this.selectedUser = user;
      this.passwordForm = {
        newPassword: ''
      };
      this.passwordDialog = true;
      this.$nextTick(() => {
        if (this.$refs.passwordForm) {
          this.$refs.passwordForm.resetValidation();
        }
      });
    },
    async savePassword() {
      if (!this.$refs.passwordForm.validate()) return;

      this.isSaving = true;
      const response = await accountService.resetPassword({
        userId: this.selectedUser.id,
        newPassword: this.passwordForm.newPassword
      });
      this.isSaving = false;

      if (response.status != 200) {
        await this.showError(response);
        return;
      }

      this.passwordDialog = false;
    },
    async removeUser(user) {
      if (!confirm(`Delete ${user.email}?`)) return;

      this.isLoading = true;
      const response = await accountService.remove(user.id);
      this.isLoading = false;

      if (response.status != 200) {
        await this.showError(response);
        return;
      }

      await this.load();
    },
    async showError(response) {
      const text = await response.text();
      try {
        const json = JSON.parse(text);
        alert(Array.isArray(json) ? json.join('\n') : JSON.stringify(json));
      } catch (e) {
        alert(text || 'Error saving');
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.users-toolbar {
  align-items: center;
  display: flex;
  gap: 12px;
  margin-bottom: 16px;

  h2 {
    color: #111827;
    font-size: 24px;
    font-weight: 800;
    margin: 0;
  }
}

.users-toolbar__app {
  max-width: 280px;
}

.users-table__name {
  color: #111827;
  font-weight: 700;
}

.users-table__email {
  color: #64748b;
  font-size: 13px;
  margin-top: 2px;
}

@media (max-width: 720px) {
  .users-toolbar {
    align-items: stretch;
    flex-direction: column;

    .v-spacer {
      display: none;
    }
  }

  .users-toolbar__app {
    max-width: none;
  }
}
</style>
